import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import ProjectCodeViewer from './ProjectCodeViewer';
import { useApproveQuestion } from '@/hooks/aiReview.hook';
import { successToast, errorToast } from '@/common/toast';

const getFileContent = (fileContent) => {
  const lines = fileContent.split(/\r?\n/);
  const startIdx = lines.findIndex((line) => line.includes('[작성 영역 시작]'));
  const endIdx = lines.findIndex((line) => line.includes('[작성 영역 끝]'));

  if (startIdx === -1 || endIdx === -1 || startIdx >= endIdx) return '';

  return lines.slice(startIdx + 1, endIdx).join('\n');
};

const RESULT_LABELS = ['실패 코드', '성공 코드1', '성공 코드2'];

/**
 * 승인 요청 모달
 * 실습 파일(읽기 전용 아님) 1개만 사용
 * failAnswer: { fileName, content }
 * passAnswers: [ { fileName, content }, { fileName, content } ] - 항상 2개
 */
const ApprovalRequestModal = ({
  open,
  onClose,
  projectId,
  file,
  feedbackResults = null,
}) => {
  const practiceFile = file ?? null;
  const approveQuestionMutation = useApproveQuestion();
  const isSubmitting = approveQuestionMutation.isPending;
  const fileName = practiceFile ? (practiceFile.fileName ?? practiceFile.name ?? '') : '';
  const defaultContent = getFileContent(practiceFile?.fileContent ?? practiceFile?.content ?? '');

  const [failAnswer, setFailAnswer] = useState({ fileName, content: defaultContent });
  const [passAnswers, setPassAnswers] = useState([
    { fileName, content: defaultContent },
    { fileName, content: defaultContent },
  ]);
  const [lastFeedbackResults, setLastFeedbackResults] = useState(null);

  const resetForm = useCallback(() => {
    setFailAnswer({ fileName, content: defaultContent });
    setPassAnswers([{ fileName, content: defaultContent }, { fileName, content: defaultContent }]);
  }, [fileName, defaultContent]);

  const handleOpen = useCallback(() => {
    if (open && practiceFile) {
      const content = defaultContent;
      setFailAnswer({ fileName, content });
      setPassAnswers([{ fileName, content }, { fileName, content }]);
      setLastFeedbackResults(null);
    }
  }, [open, practiceFile, fileName, defaultContent]);

  React.useEffect(() => {
    if (open) handleOpen();
  }, [open, handleOpen]);

  const updateFailAnswer = useCallback((field, value) => {
    setFailAnswer((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updatePassAnswer = useCallback((index, field, value) => {
    setPassAnswers((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const handleApprovalSubmit = useCallback(
    async (payload) => {
      try {
        const res = await approveQuestionMutation.mutateAsync({
          questionId: projectId,
          ...payload,
        });
        if (res.approved) {
          successToast('승인 요청이 완료되었습니다.');
          onClose();
          return true;
        }
        const results = res.results ?? [];
        setLastFeedbackResults(results);
        const failedIndexes = results.reduce((acc, result, index) => {
          if (!result.passed) acc.push(index);
          return acc;
        }, []);        
        throw new Error(`승인 요청에 실패했습니다. ${failedIndexes.map((i) => RESULT_LABELS[i]).join(', ')} 결과가 기대와 다릅니다.`);
      } catch (err) {
        errorToast(`승인 요청에 실패했습니다. - ${err.message}`);
        return false;
      }
    },
    [projectId, approveQuestionMutation, onClose]
  );

  const handleSubmit = useCallback(async () => {
    const payload = {
      failAnswer: {
        fileName,
        content: failAnswer.content ?? '',
      },
      passAnswers: passAnswers.slice(0, 2).map((p) => ({
        fileName,
        content: p.content ?? '',
      })),
    };
    const success = await handleApprovalSubmit(payload);
    if (success) {
      resetForm();
    }
  }, [fileName, failAnswer, passAnswers, handleApprovalSubmit, resetForm]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  if (!practiceFile) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>승인 요청</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            실습 파일(수정 가능 파일)이 없습니다. 파일을 먼저 등록해주세요.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>닫기</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>승인 요청</DialogTitle>
      <DialogContent>
        <Stack spacing={4} sx={{ mt: 1 }}>
          {(lastFeedbackResults ?? feedbackResults)?.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                승인 실패 원인 분석
              </Typography>
              <Stack spacing={1}>
                {(lastFeedbackResults ?? feedbackResults).map((result, idx) => (
                  <Box key={idx}>
                    <Typography variant="body2" fontWeight={500} color="error.dark">
                      <span style={{ color: result.passed ? 'green' : 'red' }}>
                        {RESULT_LABELS[idx] ?? `항목 ${idx + 1}`}: {result.passed ? '통과' : '실패'}
                      </span>
                    </Typography>
                    {result.feedback && (
                      <Typography variant="body2" sx={{ mt: 0.5, pl: 1, color: 'text.secondary' }}>
                        {result.feedback}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              실패 코드 (failAnswer) - {fileName}
            </Typography>
            <Box sx={{ height: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <ProjectCodeViewer
                key={`fail-${fileName}`}
                fileName={failAnswer.fileName}
                content={failAnswer.content}
                readOnly={false}
                onChange={(v) => updateFailAnswer('content', v ?? '')}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              성공 코드 (passAnswers) - {fileName}
            </Typography>
            <Stack spacing={3}>
              {passAnswers.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    성공 예시 {idx + 1}
                  </Typography>
                  <Box sx={{ height: 240, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    <ProjectCodeViewer
                      key={`pass-${idx}-${fileName}`}
                      fileName={item.fileName}
                      content={item.content}
                      readOnly={false}
                      onChange={(v) => updatePassAnswer(idx, 'content', v ?? '')}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>취소</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? '승인 요청 중...' : '승인 요청'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalRequestModal;
